import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { coreSync } from '../lib/core';
import { toCoreEntry, toCoreExpiries } from './packageShape';
import { todayISO } from '../domain/clock';
import { freeId } from '../domain/freeId';
import { packageSaleDocuments } from '../domain/packageSale';
import { useCollectionWindow } from './useCollectionWindow';

// Prepaid packages. Purchases also land on the family's money ledger, because
// selling a block is a charge like any other — the package ledger tracks the
// *time*, the money ledger tracks the *money*, and conflating them would make
// one of the two wrong.
//
// The rules live in `core/src/packages.rs` (#214). This file is where the
// documents are shaped for the crossing.
//
// **The unit is minutes (#181)**, and a sale is made from a template — see
// `src/domain/packageTemplates.js` for what MMS does.

const PackagesContext = createContext(null);
const TRACK_LIMIT = 5000;

export function PackagesProvider({ children }) {
  const { user } = useAuth();

  // The package ledger window, from whichever read path serves this session
  // (#207 U5). `withMetadata` keeps the snapshot path's `includeMetadataChanges`.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.packageEntries,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const entries = read.docs;

  const byStudent = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!m.has(e.studentId)) m.set(e.studentId, []);
      m.get(e.studentId).push(e);
    }
    return m;
  }, [entries]);

  const value = useMemo(() => ({
    entries,
    // Exposed because every window hook has it, not for any one caller;
    // nothing turns on this flag here (a delete asks the server, #318).
    loading: read.loading,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: read.failed,
    forStudent: (id) => byStudent.get(id) || [],

    // Has this student ever actually bought a package? (#384)
    //
    // **`forStudent(id).length > 0` is not that question.** Every kind lives in
    // this collection — a `consume` is written when attendance is marked — so
    // recording one lesson as *Absent, Issue Make-Up Credit* writes a `consume`
    // and voids it in the same save, which would list a student who never
    // bought anything on Package Balances.
    //
    // So the entry must be a **purchase** and **live**. The tombstone half goes
    // through the core, because that predicate is two fields and reading only
    // `voided` is the bug in #70 — the same call as `isEntryActive` below.
    //
    // Not `summaryFor(id).purchased > 0`: a purchase of no minutes is still a
    // purchase.
    holdsPackage: (id) => coreSync().holdsPackage((byStudent.get(id) || []).map(toCoreEntry)),

    // The core orders the log and hands back **ids**; the documents stay here.
    //
    // They carry a note, a price and the pending-write flag, none of which
    // `PackageEntry` has. Rejoining by id is what the schedule and conflict
    // splits do — see core-ffi's crate header on the flat rule. An id the core
    // returns is always one it was given, so the lookup cannot miss.
    activityFor: (id) => {
      const docs = byStudent.get(id) || [];
      const byId = new Map(docs.map((e) => [e.id, e]));
      return coreSync().packageActivityIds(docs.map(toCoreEntry)).map((x) => byId.get(x));
    },
    summaryFor: (id) => coreSync().packageSummary((byStudent.get(id) || []).map(toCoreEntry)),

    // The running-out surface (#181): the entries this student has, folded
    // against the lessons already booked for them.
    //
    // The bookings come from the caller, because an occurrence is expanded from
    // a series by `useEvents` — see `useStudentPackageForecast`, which shapes an
    // occurrence into a `BookedLesson`. What comes back is ids and balances,
    // rejoined by the caller as `activityFor` above does.
    //
    // `today` and `horizon` come from the caller too: the horizon is the
    // occurrence window `useStudentAttendance` folded. They let the fold
    // separate a past unmarked lesson from an upcoming one and let a screen say
    // how far the forecast could see (#347).
    forecastFor: (id, booked, today, horizon) => {
      const docs = byStudent.get(id) || [];
      return coreSync().packageForecast(
        docs.map(toCoreEntry),
        booked,
        // The expiry dates the sale stored (#368). A second projection off the
        // same documents rather than a field on the entry — see `toCoreExpiries`.
        toCoreExpiries(docs),
        today,
        horizon,
      );
    },

    // What each package this student holds has left, and when it lapses (#368).
    //
    // In **draw order**, which is the part a teacher cannot work out from a
    // list of sales: first in the list is the package the next lesson spends.
    // The rule is `core/src/packages.rs`'s and it is ours rather than MMS's.
    statesFor: (id) => {
      const docs = byStudent.get(id) || [];
      return coreSync().packageStates(
        docs.map(toCoreEntry),
        toCoreExpiries(docs),
        todayISO(),
      );
    },

    // The balance the attendance decision reads.
    //
    // **`packageRemainingOn` and not `packageSummary` (#368)**: time in a
    // package whose expiry date has passed is forfeited, so marking a lesson
    // against it must write a charge rather than spend time that is gone. The
    // clock is read here, unlike `forecastFor` above, whose `today` has to be
    // the one the occurrence window was folded against.
    //
    // `packageSummary` is clock-free; the two are the same number for a studio
    // that never set `Days to Expire After Start Date` on a template.
    remainingFor: (id) => {
      const docs = byStudent.get(id) || [];
      return coreSync().packageRemainingOn(
        docs.map(toCoreEntry),
        toCoreExpiries(docs),
        todayISO(),
      );
    },

    // The tombstone predicate, for a caller holding one document.
    //
    // Exposed through this context rather than reached for directly so that a
    // caller never has to know a document needs shaping first — see
    // packageShape.js for what happens when it is skipped.
    isEntryActive: (entry) => coreSync().packageEntryIsActive(toCoreEntry(entry)),

    // ── Selling ────────────────────────────────────────────────────────────
    //
    // One batch: the time it buys, the money it costs, and — if the teacher
    // left MMS's default radio alone — the already-taught lessons it converts.
    // Splitting them risks a package with no charge, or a charge deleted for a
    // package that never landed.
    //
    // **`template` is required and the price comes off it** (#181): a teacher
    // selling a package picks a template and a start date. `chargeMinor` is the
    // `Customize Charge` override — MMS's opt-in per-sale amount, price only and
    // sale only — and is `null` when the toggle was left off, meaning "the
    // template's price". A number equal to the template's price produces the
    // same documents.
    //
    // **`convert` is the destructive half, copied from MMS.** In MMS a package
    // backdated before a lesson that was already taught and charged converts
    // that lesson and **deletes its charge**. The boundary is the *package
    // start date*, not today, and a not-billable event is skipped; MMS's wizard
    // previews the charge and lists nothing it will convert. So **a charge a
    // family may already have been billed for is removed and nothing says so**
    // — MMS's behaviour, not a bug.
    //
    // The caller supplies the occurrences to convert (the division
    // `forecastFor` makes above), already filtered to lessons that were
    // marked, are billable, price off the student's own rate, and fall on or
    // after the start date; the sell wizard states that filter.
    sellPackage: async ({
      studentId, familyId, template, startOn, chargeMinor = null, note = '', teacherName = '',
      convert = [],
    }) => {
      if (!user || !template) return null;
      const pkgId = freeId('pkg');
      const batch = writeBatch(db);
      // The documents, in batch order, are `packageSaleDocuments`
      // (src/domain/packageSale.js): the purchase — its name, price and duration
      // **copied, not joined**, since renaming or deleting the template moves
      // nothing on a completed sale in MMS, with its expiry date (#368) — then
      // the package charge dated the **package start date**, as MMS dates it;
      // then, per converted lesson, the pair `setAttendance` writes for a lesson
      // that draws on package time: the consume live and the charge tombstoned,
      // both merging so a re-run is idempotent.
      //
      // **That tombstone is the line that deletes money a family may already
      // have been billed**, and it is a complete, valid entry rather than a
      // bare `{ voided }` merge: a merge-set against a document that does not
      // exist is a *create*, so a lesson whose charge was never written would
      // produce a ledger row with no kind, no amount and no family — which the
      // rules reject, taking the whole sale down with it.
      const writes = packageSaleDocuments({
        pkgId, uid: user.uid, studentId, familyId, template, startOn, chargeMinor, note,
        teacherName, convert,
      }, serverTimestamp());
      for (const w of writes) {
        batch.set(
          docRef(user.uid, COLLECTIONS[w.collection], w.id),
          w.data,
          ...(w.merge ? [{ merge: true }] : []),
        );
      }

      await batch.commit();
      return pkgId;
    },
  }), [entries, read.loading, read.failed, byStudent, user]);

  return <PackagesContext.Provider value={value}>{children}</PackagesContext.Provider>;
}

// `Days to Expire After Start Date` resolved against the sale's start date is
// `expiryDateOf` in src/domain/packageSale.js. As in MMS, the clock runs from
// the start date, backdated or not: a 30-day template starting 9/1 expires
// 10/1.

export function usePackages() {
  const ctx = useContext(PackagesContext);
  if (!ctx) throw new Error('usePackages must be used within a PackagesProvider');
  return ctx;
}
